import { useMemo } from 'react';
import type { PreviewData, SelectedScreenMeta } from '@/features/campaigns/types/campaign';
import { idFromRow, screenNameFromRow } from './previewStepHelpers';
import type { BillboardRow } from './PreviewStep.types';

function locationFromMeta(meta?: SelectedScreenMeta): string {
  if (!meta) return '';
  const parts = [meta.addressLine, meta.city].filter(Boolean);
  if (parts.length) return parts.join(', ');
  return meta.city || '';
}

/** Builds the selected-screens table rows from the pricing preview payload (or a fallback of the raw selection meta). */
export function useBillboardRows(
  data: PreviewData,
  selectedScreensMeta: Record<string, SelectedScreenMeta>,
): BillboardRow[] {
  const rawScreens = useMemo(
    () =>
      Array.isArray(data.billboardWise)
        ? (data.billboardWise as Array<Record<string, unknown>>)
        : [],
    [data.billboardWise],
  );

  const repsByScreenName = useMemo(() => {
    const distribution = data.distributionInfo;
    if (!distribution || typeof distribution !== 'object') return null;
    const map: Record<string, number> = {};
    Object.values(distribution).forEach((hour) => {
      const list = (hour as { billboardDistribution?: Array<Record<string, unknown>> })
        ?.billboardDistribution;
      if (!Array.isArray(list)) return;
      list.forEach((dist) => {
        const name = String(dist?.screenName || '');
        if (!name) return;
        map[name] = (map[name] || 0) + Number(dist.assignedRepetitions || 0);
      });
    });
    return map;
  }, [data.distributionInfo]);

  return useMemo(() => {
    if (rawScreens.length) {
      return rawScreens.map((row, idx) => {
        const id = idFromRow(row);
        const meta = id ? selectedScreensMeta[id] : undefined;
        const name =
          screenNameFromRow(row, meta) || meta?.screenName || `Screen ${idx + 1}`;
        const assigned = row.assignedRepetitions;
        const reps =
          assigned != null ? Number(assigned) : repsByScreenName?.[name] ?? null;
        const price = Number(
          row.finalAmount ?? row.amountWithTax ?? row.amountWithoutTax ?? 0,
        );
        return {
          key: id || String(idx),
          screenName: name,
          idTail: id ? id.slice(-8) : '—',
          location: locationFromMeta(meta) || meta?.city || '—',
          reps: Number.isFinite(reps as number) ? (reps as number) : null,
          price,
          thumbnail: meta?.thumbnail || undefined,
          orientation: meta?.screenOrientation || undefined,
        };
      });
    }

    return Object.values(selectedScreensMeta).map((meta, idx) => ({
      key: meta.id || String(idx),
      screenName: meta.screenName || `Screen ${idx + 1}`,
      idTail: meta.id ? meta.id.slice(-8) : '—',
      location: locationFromMeta(meta) || '—',
      reps: null,
      price: 0,
      thumbnail: meta.thumbnail || undefined,
      orientation: meta.screenOrientation || undefined,
    }));
  }, [rawScreens, selectedScreensMeta, repsByScreenName]);
}
